import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { investorsAPI } from '../../services/api';

const InvestorManagement = () => {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    setIsLoading(true);
    try {
      const response = await investorsAPI.getAll();
      setInquiries(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await investorsAPI.updateStatus(id, newStatus);
      setInquiries(inquiries.map(inq => 
        inq.id === id ? { ...inq, status: newStatus } : inq
      ));
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'En attente' },
      contacted: { color: 'bg-blue-500/20 text-blue-400', icon: Mail, label: 'Contacté' },
      closed: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Clôturé' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50 animate-fadeIn">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="w-6 h-6 text-blue-400" />
        <h2 className="text-2xl font-bold text-white">Demandes Investisseurs</h2>
      </div>

      <div className="space-y-4">
        {inquiries.length === 0 ? (
          <p className="text-gray-400 text-center py-8" data-testid="no-inquiries-msg">
            Aucune demande disponible
          </p>
        ) : (
          inquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="bg-slate-700/30 rounded-lg p-6 hover:bg-slate-700/50 transition-colors"
              data-testid={`inquiry-item-${inquiry.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{inquiry.name}</h3>
                  <p className="text-sm text-gray-400">{inquiry.email}</p>
                  {inquiry.company && (
                    <p className="text-sm text-gray-500">{inquiry.company}</p>
                  )}
                </div>
                {getStatusBadge(inquiry.status)}
              </div>
              <p className="text-gray-300 mb-4">{inquiry.message}</p>
              <div className="flex gap-2">
                {inquiry.status !== 'contacted' && (
                  <Button
                    onClick={() => handleStatusUpdate(inquiry.id, 'contacted')}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="mark-contacted-btn"
                  >
                    Marquer comme contacté
                  </Button>
                )}
                {inquiry.status !== 'closed' && (
                  <Button
                    onClick={() => handleStatusUpdate(inquiry.id, 'closed')}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="mark-closed-btn"
                  >
                    Clôturer
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InvestorManagement;